import React from "react";

import { Filter, Project } from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useZonesRetrieval,
} from "@planzone/view-models";
import { Calendar } from "@scenes/calendar";
import { useCalendarData } from "@scenes/calendar/useCalendarData";
import { SideBar } from "@pages/calendarPage/sideBar";
import { usePreferences } from "@components/preferences";
import { Props } from "./types";
import styles from "./styles.scss";

function ZoneCalendarPage({ zone }: Props) {
  const { state, edit } = usePreferences();
  const filter = state.zoneCalendarFilter;
  const setFilter = (nextFilter: Filter) =>
    edit({ ...state, zoneCalendarFilter: { ...filter, ...nextFilter } });

  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    zones,
    isRequestPending: isZonesRequestPending,
    isRequestSuccess: isZonesRequestSuccess,
    isRequestFailure: isZonesRequestFailure,
    retrieveZones,
  } = useZonesRetrieval(planzone);

  const zoneProjects = React.useMemo(
    () => zones.find((z) => z.uuid === zone.uuid)?.projects ?? [],
    [zones, zone.uuid],
  );

  const selectedProjectsUuids = (filter.projects || []).map((p) => p.uuid);

  const toggleProjectFilter = React.useCallback(
    (projectUuid: string) => {
      const currentProjects = filter.projects || [];
      const isSelected = currentProjects.some((p) => p.uuid === projectUuid);

      const newProjects = isSelected
        ? currentProjects.filter((p) => p.uuid !== projectUuid)
        : ([
            ...currentProjects,
            zoneProjects.find((p) => p.uuid === projectUuid),
          ].filter(Boolean) as Project[]);

      setFilter({ ...filter, projects: newProjects });
    },
    [filter, zoneProjects],
  );

  const calendarFilter = React.useMemo(
    () => ({
      zones: [zone],
      projects: filter.projects,
      resources: filter.resources,
      projectPortfolios: filter.projectPortfolios,
      projectStatuses: filter.projectStatuses,
      projectsTags: filter.projectsTags,
      elementTypes: filter.elementTypes,
      scope: "ZONE" as const,
    }),
    [
      zone.uuid,
      filter.projects,
      filter.resources,
      filter.projectPortfolios,
      filter.projectStatuses,
      filter.projectsTags,
      filter.elementTypes,
      filter.scope,
    ],
  );

  const {
    cursor,
    setCursor,
    hasPlanzone,
    isRequestPending,
    events,
    tasks,
    calendar,
    weeks,
    currentWeek,
  } = useCalendarData(calendarFilter);

  React.useEffect(() => {
    !isZonesRequestPending &&
      !isZonesRequestSuccess &&
      !isZonesRequestFailure &&
      retrieveZones(planzone);
  }, [
    planzone.uuid,
    isZonesRequestPending,
    isZonesRequestSuccess,
    retrieveZones,
  ]);

  return (
    <div className={styles.calendarPage}>
      <SideBar
        projects={zoneProjects}
        isRequestPending={isZonesRequestPending}
        selectedProjects={selectedProjectsUuids}
        onToggleProject={toggleProjectFilter}
      />
      <div className={styles.calendar}>
        <Calendar
          date={cursor}
          events={events}
          milestones={calendar.milestones}
          tasks={tasks}
          editDate={setCursor}
          zones={[zone]}
          isRequestPending={isRequestPending}
          month={weeks}
          currentWeek={currentWeek}
          renderDay={(date, events) => (
            <div>
              {events.map((event) => (
                <div key={event.uuid}>
                  <p>{event.name}</p>
                </div>
              ))}
            </div>
          )}
          planzone={planzone}
          hasPlanzone={hasPlanzone}
          filter={filter}
          setFilter={setFilter}
          scope="zone"
        />
      </div>
    </div>
  );
}

export { ZoneCalendarPage };
